import type { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api';

export type PdfJs = typeof import('pdfjs-dist');
export type PdfViewerLib = typeof import('pdfjs-dist/web/pdf_viewer.mjs');

interface IPdfJsBundle {
    pdfjs: PdfJs;
    PdfWorker: new () => Worker;
}

let pdfjsPromise: Promise<IPdfJsBundle> | undefined;
let viewerPromise: Promise<PdfViewerLib> | undefined;

/** pdf.js (~1,7 МБ с воркером) грузится лениво — только когда на экране появился PDF.
 *  Воркер — через `?worker`: Vite собирает его обычным same-origin `.js` (под CSP
 *  `script-src 'self'`), а не `.mjs`, который старый nginx отдаёт не JS-типом. */
export const loadPdfJs = (): Promise<IPdfJsBundle> => {
    pdfjsPromise ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?worker')]).then(
        ([pdfjs, worker]) => ({ pdfjs, PdfWorker: worker.default }),
    );
    // неудачную загрузку чанка не кэшируем — следующее открытие попробует снова
    pdfjsPromise.catch(() => (pdfjsPromise = undefined));
    return pdfjsPromise;
};

/** Компоненты просмотрщика pdf.js (PDFViewer, поиск, ссылки). Модуль при загрузке берёт
 *  ядро из `globalThis.pdfjsLib`, поэтому ядро подкладываем туда до импорта. */
export const loadPdfViewerLib = (): Promise<PdfViewerLib> => {
    viewerPromise ??= loadPdfJs().then(({ pdfjs }) => {
        (globalThis as { pdfjsLib?: PdfJs }).pdfjsLib = pdfjs;
        return import('pdfjs-dist/web/pdf_viewer.mjs');
    });
    viewerPromise.catch(() => (viewerPromise = undefined));
    return viewerPromise;
};

/** Ресурсы pdf.js раздаются самим приложением (`{base}pdfjs/`, копирует `organisms/FilePreview/vitePlugin.js`):
 *  у части клиентов нет интернета, CDN исключён. База CP — `/controlpanel` без завершающего слэша. */
const assetUrl = (dir: string): string => `${import.meta.env.BASE_URL.replace(/\/?$/, '/')}pdfjs/${dir}/`;

/**
 * Общие параметры открытия документа. cMaps и стандартные шрифты нужны PDF без встроенных
 * шрифтов (сканы из МФУ, старые генераторы), WASM-декодеры — картинкам JPEG 2000/JBIG2 в сканах;
 * где CSP запрещает WASM, pdf.js сам берёт JS-замену из той же папки.
 * XFA-формы не исполняются: документ приходит от внешних пользователей, а скрипты
 * (pdf.sandbox) мы не подключаем вовсе.
 */
export const pdfDocumentParams = (): Partial<DocumentInitParameters> => ({
    cMapUrl: assetUrl('cmaps'),
    cMapPacked: true,
    standardFontDataUrl: assetUrl('standard_fonts'),
    wasmUrl: assetUrl('wasm'),
    iccUrl: assetUrl('iccs'),
    enableXfa: false,
    stopAtErrors: false,
});

/** Воркер pdf.js в `PDFWorker`-обёртке. Порт гасим сами: внешний порт `PDFWorker` не закрывает. */
export const createPdfWorker = (pdfjs: PdfJs, PdfWorker: new () => Worker) => {
    const port = new PdfWorker();
    // в d.ts pdf.js `port` ошибочно типизирован как null — рантайм принимает Worker
    const worker = new pdfjs.PDFWorker({ port } as unknown as ConstructorParameters<PdfJs['PDFWorker']>[0]);
    return {
        worker,
        destroy: () => {
            worker.destroy();
            port.terminate();
        },
    };
};
